import * as React from 'react';

import {
  Grid,
  AutocompleteChangeReason,
  SelectChangeEvent,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Box,
  TextField,
  ThemeProvider,
} from '@mui/material';

import { Controller, FieldValues, UseControllerProps, UseFormSetValue } from 'react-hook-form';

import _startCase from 'lodash/startCase';
import { NestedOption } from '../core/api/clients';
import EditableGroup from './EditableGroup';
import useEditableControllers from '../core/api/hooks/common/useEditableControllers';
import { useFormUpdateProxy } from '../core/context/FormUpdateProxyContext';
import { GroupedFieldTheme } from '../core/styles/theme';
import ReadOnlyGroup from './ReadOnlyGroup';

export type ControlledCascadingSelectChangeDetails = {
  changeReason: AutocompleteChangeReason,
  values: Array<{
    fieldName: string,
    value: string,
  }>
};

interface IControlledCascadingSelect<TFieldValues extends FieldValues> extends UseControllerProps<TFieldValues> {
  groupName: string,
  fieldNames: string[]
  options: NestedOption[] | null | undefined,
  readOnly?: boolean,
  setValue: UseFormSetValue<TFieldValues>,
  disableEditPopper?: boolean,
}

function ControlledCascadingSelect<TFieldValues extends FieldValues>({
  groupName,
  control,
  fieldNames,
  options,
  readOnly,
  rules,
  setValue,
  disableEditPopper,
}: IControlledCascadingSelect<TFieldValues>) {
  const { watchedValues, revert, commit } = useEditableControllers<TFieldValues>({
    fieldNames,
    control,
  });
  const [values, setValues] = React.useState<NestedOption[]>([]);
  const [activeIndex, setActiveIndex] = React.useState<number>(0);
  const { requestUpdate } = useFormUpdateProxy();
  React.useEffect(() => {
    setValues((watchedValues as string[]).reduce((
      accumulator: NestedOption[],
      currentValue: string,
      currentIndex: number,
    ) => {
      const searchTarget = currentIndex === 0
        ? options
        : accumulator[currentIndex - 1]?.nestedOptions || [] as NestedOption[];

      if (searchTarget && searchTarget.length !== 0) {
        const isValueMatch = (nestedOption: NestedOption) => nestedOption.value === currentValue;
        const nextValue = searchTarget.find(isValueMatch);

        if (nextValue !== undefined) {
          return [...accumulator, nextValue];
        }
      }

      return accumulator;
    }, [] as NestedOption[]));
  }, [watchedValues, options]);

  // If the final field in the cascade has a single option, set that option
  React.useEffect(() => {
    if (Array.isArray(values) && Array.isArray(fieldNames) && values.length > 0) {
      const { nestedOptions } = values.slice(-1)[0];
      if (nestedOptions && nestedOptions.length === 1) {
        setValue(fieldNames[values.length] as any, nestedOptions[0].value as any);
      }
    }
  }, [values, fieldNames]);

  const handleCascadedSelectChange = (event: SelectChangeEvent<string>, index: number) => {
    // Clear all values ahead of the currently changed selection
    if (index < fieldNames.length - 1) {
      fieldNames.slice(index + 1).forEach((value: string) => {
        setValue(
          value as any,
          '' as any,
        );
        setActiveIndex(index);
      });
    }
  };

  const getMenuItems = (index: number): NestedOption[] => {
    if (options && index === 0) {
      return options;
    }

    if (values) {
      const currentValues = values[index - 1];
      if (currentValues && currentValues.nestedOptions) {
        return currentValues.nestedOptions;
      }
    }

    return [];
  };
  return (

    <EditableGroup
      fieldNames={fieldNames}
      disableEditPopper={disableEditPopper}
      renderMode={readOnly ? 'read' : 'edit'}
      activeIndex={activeIndex}
      renderEditFields={() => (
        <Box border={1} borderColor="grey.500" borderRadius={1} pt={2} px={2}>
          <Grid container spacing={2}>
            <ThemeProvider theme={GroupedFieldTheme}>
              {fieldNames.map((fieldName: string, index: number) => (
                <Grid
                  item
                  key={fieldName}
                  xs={12}
                  sm={fieldNames.length < 3}
                  md={12 / fieldNames.length}
                >
                  <Controller
                    name={fieldName as any}
                    control={control}
                    render={({ field, fieldState }) => (
                      <FormControl
                        variant="outlined"
                        fullWidth
                        error={fieldState.invalid}
                        required={rules?.required === true ?? false}
                        disabled={index > 0 && (!watchedValues || watchedValues[index - 1] === null)}
                      >
                        <InputLabel id={`lbl-${fieldName}`}>
                          {`${_startCase(fieldName)}`}
                        </InputLabel>
                        <Select
                          variant="standard"
                          labelId={`lbl-${fieldName}`}
                          label={`${_startCase(fieldName)}`}
                          value={field.value as any || ''}
                          onChange={(event: SelectChangeEvent<string>) => {
                            field.onChange(event);
                            handleCascadedSelectChange(event, index);
                          }}
                          onBlur={field.onBlur}
                        >
                          {getMenuItems(index).map((nestedOption) => ({
                            value: nestedOption.value ?? '',
                            name: nestedOption.value ?? '',
                          })).map((menuItem) => (
                            menuItem.value
                              ? (
                                <MenuItem key={menuItem.value} value={menuItem.value}>
                                  {menuItem.name}
                                </MenuItem>
                              )
                              : (
                                <MenuItem value="">
                                  <em>{menuItem.name}</em>
                                </MenuItem>
                              )
                          ))}
                        </Select>
                        {fieldState.invalid && fieldState.error && (
                        <FormHelperText>{fieldState.error?.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
              ))}
            </ThemeProvider>
          </Grid>
        </Box>
      )}
      renderReadFields={() => (
        <ReadOnlyGroup
          name={groupName}
          render={() => (
            <Grid container spacing={2}>
              {fieldNames.map((fieldName: string, index: number) => (
                <Grid
                  item
                  key={fieldName}
                >
                  <Controller
                    key={fieldName}
                    name={fieldName as any}
                    control={control}
                    render={({ field }) => (
                      <Box>
                        {`${field.value}${(index < fieldNames.length - 1) ? ' >' : ''}`}
                      </Box>
                    )}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        />
      )}
      saveSuccess={() => {
        commit();
        requestUpdate();
      }}
      onEditCancel={() => {
        revert();
      }}
    />
  );
}

export default ControlledCascadingSelect;
